import { lastValueFrom } from 'rxjs';

/* eslint-disable @typescript-eslint/no-empty-function */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { FileApiClient } from '@ideapa/app-api-client';
import { FileDto } from '@ideapa/common-dto';
import { isImage } from '@ideapa/common-utils';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap/modal';

let nextId = 0;

@Component({
  standalone: false,
  selector: 'ideapa-file-reader',
  templateUrl: './file-reader.component.html',
  styleUrls: ['./file-reader.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileReaderComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => FileReaderComponent),
      multi: true,
    },
  ],
})
export class FileReaderComponent implements OnDestroy, ControlValueAccessor {
  id = `fr-${nextId++}`;

  @ViewChild('input', { static: true })
  input?: ElementRef;

  @Input()
  accept = '*';

  @Input()
  maximumSizeMB = 5;

  @Input()
  multiple = false;

  @Input()
  loadImmediately = true;

  @Output()
  fileChange = new EventEmitter<File[]>();

  @Output()
  maxSizeError = new EventEmitter<number>();

  value?: FileDto[] = [];
  isValid = true;

  isLoading = false;

  selectedAttachment?: FileDto;

  isDisabled = false;

  // get filename() {
  //   if (!this.value) {
  //     return '';
  //   }
  //   return this.value.name.substring(this.value.name.lastIndexOf('/') + 1);
  // }

  constructor(
    private cd: ChangeDetectorRef,
    private uploadService: FileApiClient,
    private dialogService: NgbModal
  ) {}

  propagateChange = (file?: FileDto[]) => {};
  onTouched = () => {};
  validateFn: any = () => {};

  writeValue(value?: FileDto[]) {
    this.value = value ? [...value] : undefined;
    this.cd.detectChanges();
  }

  registerOnChange(fn: (value?: FileDto[]) => void) {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean) {
    this.isDisabled = isDisabled;
    this.cd.markForCheck();
  }

  validate(control: FormControl) {
    const res = this.validateFn(control);
    this.isValid = res == null;
    return res;
  }

  ngOnDestroy() {
    this.cd.detach();
  }

  async onSelectFile(event: any) {
    if (event.target.files && event.target.files.length) {
      const files = event.target.files as FileList;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.size > this.maximumSizeMB * 1024 * 1024) {
          this.maxSizeError.emit(this.maximumSizeMB);
          return;
        }
      }
      if (this.loadImmediately) {
        await this.onLoad(files);
      }
    }
  }

  getImagePreviewUrl(media: FileDto) {
    return media.url;
  }

  isImage(media: FileDto) {
    return isImage(media.mimeType);
  }

  private async onLoad(files?: FileList) {
    if (files && files.length) {
      this.isLoading = true;
      try {
        const res = await lastValueFrom(this.uploadService.upload(files));

        if (!this.value) {
          this.value = [];
        }
        this.value = [...this.value, ...res];
        this.propagateChange(this.value);
        this.onTouched();

        if (this.input) {
          this.input.nativeElement.value = null;
        }

        this.isLoading = false;
        this.cd.detectChanges();
        console.log('attachment added');
      } catch (err) {
        console.error(err);
        this.isLoading = false;
        this.cd.detectChanges();
        console.log('add attachment failed');
      }
    }
  }

  public remove(file: FileDto) {
    // this.uploadService.delete(file.id).subscribe((res) => {
    this.value = this.value?.filter((f) => f !== file);
    this.propagateChange(this.value);
    this.onTouched();
    this.cd.detectChanges();
    console.log('attachment removed');
    // });
  }

  public async download(file: FileDto) {
    const blob = await lastValueFrom(this.uploadService.download(file));
    this.downloadFile(blob, file.mimeType, file.filename);
  }

  private downloadFromUrl(url: string, name: string) {
    const link = document.createElement('a');
    link.setAttribute('target', '_blank');
    link.setAttribute('href', url);
    link.setAttribute('download', name);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  private downloadFile(data: ArrayBuffer, type: string, name: string) {
    const blob = new Blob([data], { type: type });
    const url = window.URL.createObjectURL(blob);
    this.downloadFromUrl(url, name);
    window.URL.revokeObjectURL(url);
  }

  public zoom(file: FileDto, content: any) {
    this.selectedAttachment = file;
    this.dialogService.open(content, {
      size: 'xl',
    });
  }
}
